import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { tap } from 'rxjs';
import { User } from '../../shared/models/user';
import { NgxPermissionsService } from 'ngx-permissions';
import { BaseService } from './base.service';
import { TokenManagerService } from './token-manager.service';
import { Router } from '@angular/router';
import { UsersService } from './users.service';
import { MatDialog } from '@angular/material/dialog';
import { showAlertDialog } from '../../shared/helpers/showAlertDialog';
import { AlertType } from '../../shared/models/alertType';

@Injectable({
  providedIn: 'root',
})
export class AccountService extends BaseService<User> {
  private sessionTimer?: ReturnType<typeof setTimeout>;

  constructor(
    protected override http: HttpClient,
    private tokenManager: TokenManagerService,
    private permissionsService: NgxPermissionsService,
    private userService: UsersService,
    private router: Router,
    private dialog: MatDialog
  ) {
    super(http, 'account');
  }

  login(values: { email: string; password: string }) {
    const body = {
      email: values.email,
      password: values.password,
      audience: 'urn:maresias:panel'
    };
    return this.http.post(this.baseApiUrl + '/login', body).pipe(
      tap((response: any) => {
        this.tokenManager.setToken(response.token);
        this.tokenManager.setRefreshToken(response.refreshToken);
        this.tokenManager.setTokenExpiresIn(response.expiresIn);
        this.tokenManager.setSessionDeadline(response.refreshExpiresIn);
        this.scheduleSessionTimeout();
      })
    );
  }

  logout() {
    this.clearSessionTimeout();
    this.tokenManager.removeToken();
    this.tokenManager.removeRefreshToken();
    this.tokenManager.removeTokenExpiresIn();
    this.tokenManager.removeSessionDeadline();
    this.userService.currentUser.set(null);
    this.permissionsService.flushPermissions();
    this.router.navigate(['account/login']);
  }

  /**
   * Liga o monitor de expiração de sessão. Chamado uma vez no boot do app.
   * Reagenda ao voltar pra aba (o setTimeout é throttled em aba em background).
   */
  initSessionWatch() {
    this.scheduleSessionTimeout();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.scheduleSessionTimeout();
      }
    });
  }

  /**
   * Agenda o logout proativo para o deadline da sessão (validade do refresh
   * token). Atividade gera refresh, que desliza o deadline e reagenda; sem
   * atividade, o timer dispara e desloga sozinho — sem esperar a próxima
   * requisição falhar.
   */
  private scheduleSessionTimeout() {
    this.clearSessionTimeout();

    if (!this.tokenManager.isLogged()) {
      return;
    }

    const deadline = this.tokenManager.getSessionDeadline();
    if (deadline === null) {
      return;
    }

    const msUntilExpiry = deadline - Date.now();
    if (msUntilExpiry <= 0) {
      this.handleSessionExpired();
      return;
    }

    this.sessionTimer = setTimeout(() => this.handleSessionExpired(), msUntilExpiry);
  }

  private clearSessionTimeout() {
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
      this.sessionTimer = undefined;
    }
  }

  /** Sessão expirou por inatividade: desloga e avisa o usuário. */
  private handleSessionExpired() {
    if (!this.tokenManager.isLogged()) {
      return;
    }

    this.logout();
    showAlertDialog(
      this.dialog,
      AlertType.PartialSuccess,
      'Sessão expirada',
      'Sua sessão expirou e você foi desconectado. Faça login novamente para continuar.',
    );
  }

  forgotPassword(email: string) {
    return this.http.post(this.baseApiUrl + '/request-forgot-password', { email });
  }

  resetPassword(token: string, newPassword: string) {
    return this.http.put(this.baseApiUrl + '/reset-password', {
      token,
      newPassword,
    });
  }

  verifyToken(token: string) {
    return this.http.post<boolean>(this.baseApiUrl + '/reset-password/verifyToken', { token });
  }

  refreshToken() {
    var refreshToken = this.tokenManager.getRefreshToken();
    return this.http.post(this.baseApiUrl + '/refresh-token', { refreshToken }).pipe(
      tap((response: any) => {
        this.tokenManager.setToken(response.token);
        this.tokenManager.setRefreshToken(response.refreshToken);
        this.tokenManager.setTokenExpiresIn(response.expiresIn);
        this.tokenManager.setSessionDeadline(response.refreshExpiresIn);
        this.scheduleSessionTimeout();
      })
    );
  }

  changePassword(currentPassword: string, newPassword: string) {
    return this.http.post(this.baseApiUrl + '/change-password', {
      currentPassword,
      newPassword,
    });
  }
}
